import React from "react";
import { motion } from "framer-motion";
import { Sun, Moon, Trophy, Info, CalendarClock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const DATA = {
  es: {
    badge: "La Fórmula de Tony",
    title: "Fórmula del Superatleta de Tony",
    intro: "Tengo 44 años rindiendo como en mis 20. Esta es la fórmula que uso como experimento personal en mi rendimiento físico. La consumo de 1 a 3 días por semana.",
    dayTitle: "Durante el día",
    nightTitle: "En la noche",
    freq: "Frecuencia: de 1 a 3 días por semana (uso experimental).",
    soon: "Próximamente subiré contenido a mi canal sobre cómo cada suplemento fue estudiado para mejorar ciertas funciones del rendimiento físico atlético.",
    disclaimer: "Esto es contenido educativo basado en mi investigación personal y en lo que he escuchado de expertos sobre lo que ofrece cada nutriente. No es consejo médico. No lo recomiendo para personas con enfermedades graves; consulta a tu especialista para saber si puedes consumirlo.",
    day: [
      { name: "Cordyceps", dose: "3 g", note: "Resistencia — atletas chinos destacaron en las olimpiadas consumiendo Cordyceps." },
      { name: "Creatina", dose: "5 g", note: "Batería extra para la resistencia." },
      { name: "Aceite de comino negro (black seed oil)", dose: "1 cucharadita", note: "Mejora la función respiratoria de los bronquios." },
      { name: "Extracto de cúrcuma (turmeric)", dose: "500 mg – 1 g", note: "Función antiinflamatoria." },
      { name: "Clorofila", dose: "100 mg", note: "Ayuda a desintoxicar la sangre del ácido láctico al ejercitarse." },
      { name: "Vitamina D3", dose: "10,000 UI", note: "Prohormona natural." },
      { name: "Vitamina K2 (MK-7)", dose: "100 mcg", note: "Junto con la D3, ayuda a la activación y a evitar la calcificación." },
      { name: "Magnesio glicinato", dose: "", note: "Apoya la activación de la vitamina D3." },
    ],
    night: [
      { name: "Melatonina", dose: "2 – 10 mg", note: "Reparación durante el descanso." },
    ],
  },
  en: {
    badge: "Tony's Formula",
    title: "Tony's Superathlete Formula",
    intro: "I'm 44 and performing like I did in my 20s. This is the formula I use as a personal experiment on my physical performance. I take it 1 to 3 days per week.",
    dayTitle: "During the day",
    nightTitle: "At night",
    freq: "Frequency: 1 to 3 days per week (experimental use).",
    soon: "I'll soon upload content to my channel on how each supplement has been studied to improve specific athletic performance functions.",
    disclaimer: "This is educational content based on my personal research and what I've heard from experts about what each nutrient offers. It is not medical advice. I don't recommend it for people with serious illnesses; consult your specialist to know whether you can take it.",
    day: [
      { name: "Cordyceps", dose: "3 g", note: "Endurance — Chinese athletes placed top at the olympics taking Cordyceps." },
      { name: "Creatine", dose: "5 g", note: "Extra battery for endurance." },
      { name: "Black seed oil", dose: "1 teaspoon", note: "Improves the respiratory function of the bronchi." },
      { name: "Turmeric extract", dose: "500 mg – 1 g", note: "Anti-inflammatory function." },
      { name: "Chlorophyll", dose: "100 mg", note: "Helps detox the blood from lactic acid during exercise." },
      { name: "Vitamin D3", dose: "10,000 IU", note: "Natural pro-hormone." },
      { name: "Vitamin K2 (MK-7)", dose: "100 mcg", note: "With D3, supports activation and helps avoid calcification." },
      { name: "Magnesium glycinate", dose: "", note: "Supports vitamin D3 activation." },
    ],
    night: [
      { name: "Melatonin", dose: "2 – 10 mg", note: "Recovery during rest." },
    ],
  },
};

const Row = ({ item }) => (
  <div className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
    <div className="min-w-0 flex-1">
      <p className="text-sm font-bold text-gray-800">{item.name}</p>
      <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{item.note}</p>
    </div>
    {item.dose && <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">{item.dose}</span>}
  </div>
);

export const TonyFormula = () => {
  const { lang } = useLang();
  const d = DATA[lang] || DATA.en;

  return (
    <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="mb-8 overflow-hidden rounded-2xl border border-emerald-200 bg-white" data-testid="tony-formula">
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-5 text-white">
        <span className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wider">
          <Trophy size={13} /> {d.badge}
        </span>
        <h2 className="text-xl font-bold sm:text-2xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{d.title}</h2>
        <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-emerald-50">{d.intro}</p>
      </div>

      <div className="grid gap-5 p-6 lg:grid-cols-2">
        <div>
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-amber-600"><Sun size={15} /> {d.dayTitle}</h3>
          <div className="space-y-2">{d.day.map((it, i) => <Row key={i} item={it} />)}</div>
        </div>
        <div>
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-indigo-500"><Moon size={15} /> {d.nightTitle}</h3>
          <div className="space-y-2">{d.night.map((it, i) => <Row key={i} item={it} />)}</div>
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-800">
            <CalendarClock size={14} className="mt-0.5 shrink-0" /> {d.freq}
          </div>
        </div>
      </div>

      <div className="space-y-3 border-t border-gray-100 px-6 pb-6 pt-4">
        <p className="text-xs leading-relaxed text-gray-500">{d.soon}</p>
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-800">
          <Info size={14} className="mt-0.5 shrink-0" /> {d.disclaimer}
        </div>
      </div>
    </motion.section>
  );
};

export default TonyFormula;
